// SanctionRuleFormModal.jsx
import React, { useEffect, useId, useMemo, useState } from "react";
import { GlobalStyles } from "../../constants/styles";
import { alphaFromHex } from "../../util/colors";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Modal used to EDIT an existing sanction rule.
 *
 * Props:
 * - rule: sanctionRule object
 * - onSave: async (input) => Promise<void>
 * - onClose: () => void
 */
export default function SanctionRuleFormModal({ rule, onSave, onClose }) {
    const idPrefix = useId();
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [name, setName] = useState(rule?.name || "");
    const [maxYellow, setMaxYellow] = useState(rule?.maxAccumulatedYellowCards ?? 4);
    const [redSusp, setRedSusp] = useState(rule?.redCardSuspensionGames ?? 1);
    const [yellowSusp, setYellowSusp] = useState(rule?.yellowCardSuspensionGames ?? 1);
    const [doubleYellowSusp, setDoubleYellowSusp] = useState(rule?.doubleYellowCardSuspensionGames ?? 1);

    const [resetOnRed, setResetOnRed] = useState(rule?.resetYellowCardsOnRed ?? true);
    const [resetOnDoubleYellow, setResetOnDoubleYellow] = useState(rule?.resetYellowCardsOnDoubleYellow ?? true);
    const [overrideAccum, setOverrideAccum] = useState(rule?.doubleYellowCardOverridesAccumulation ?? true);
    const [resetOnStageChange, setResetOnStageChange] = useState(rule?.resetYellowCardsOnStageChange ?? true);
    const [resetSanctions, setResetSanctions] = useState(rule?.resetSanctionsOnStageChange ?? false);

    const [yellowFine, setYellowFine] = useState(rule?.yellowCardFine ?? "");
    const [redFine, setRedFine] = useState(rule?.redCardFine ?? "");
    const [doubleYellowFine, setDoubleYellowFine] = useState(rule?.doubleYellowCardFine ?? "");

    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, []);

    const ui = useMemo(() => {
        const canary = GlobalStyles?.colors?.canary || "var(--kbs-primary, #00adff)";
        const woodsmoke = GlobalStyles?.colors?.woodsmoke || "#14151a";

        return {
            backdrop: {
                backgroundColor: "rgba(0,0,0,0.55)",
            },
            dialog: {
                maxWidth: 920,
            },
            content: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },

            header: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 40,
                height: 40,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },

            body: {
                padding: 14,
                background: "#fff",
            },

            section: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                padding: 14,
            },

            grid2: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            grid3: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 12,
            },

            label: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.85)",
                marginBottom: 6,
                display: "block",
            },
            input: {
                borderRadius: 14,
                border: `1px solid ${colors.primary}`,
                padding: "10px 12px",
                fontWeight: 800,
                background: "#fff",
                color: "#14151a",
            },
            hint: {
                marginTop: 6,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            toggleCard: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                padding: "12px 12px",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
            },
            toggleText: { minWidth: 0 },
            toggleTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 13.5,
                color: "rgba(20,21,26,0.82)",
            },
            toggleSub: {
                margin: "4px 0 0 0",
                fontWeight: 700,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.55)",
            },

            checkbox: {
                width: 18,
                height: 18,
                marginTop: 2,
                flex: "0 0 auto",
                accentColor: colors.primary,
                cursor: "pointer",
            },

            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnMuted: {
                background: "rgba(0,0,0,0.02)",
            },
            btnPrimary: {
                background: canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const toInt = (v, fallback) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : fallback;
    };

    const toFloatOrNull = (v) => {
        if (v === "" || v === null || typeof v === "undefined") return null;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        const input = {
            name: name?.trim(),
            maxAccumulatedYellowCards: toInt(maxYellow, 4),
            redCardSuspensionGames: toInt(redSusp, 1),
            yellowCardSuspensionGames: toInt(yellowSusp, 1),
            doubleYellowCardSuspensionGames: toInt(doubleYellowSusp, 1),
            resetYellowCardsOnRed: !!resetOnRed,
            resetYellowCardsOnDoubleYellow: !!resetOnDoubleYellow,
            doubleYellowCardOverridesAccumulation: !!overrideAccum,
            resetYellowCardsOnStageChange: !!resetOnStageChange,
            resetSanctionsOnStageChange: !!resetSanctions,
            yellowCardFine: toFloatOrNull(yellowFine),
            redCardFine: toFloatOrNull(redFine),
            doubleYellowCardFine: toFloatOrNull(doubleYellowFine),
        };

        try {
            await onSave(input);
        } finally {
            setUploading(false);
            onClose();
        }
    };

    const ToggleCard = ({ id, checked, onChange, title, description }) => (
        <label htmlFor={id} style={ui.toggleCard}>
            <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={onChange}
                style={ui.checkbox}
            />
            <div style={ui.toggleText}>
                <p style={ui.toggleTitle}>{title}</p>
                <p style={ui.toggleSub}>{description}</p>
            </div>
        </label>
    );

    return (
        <div className="modal fade show d-block" style={ui.backdrop} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-scrollable" style={ui.dialog} role="document">
                <div className="modal-content" style={ui.content}>
                    <form onSubmit={handleSubmit}>
                        <div style={ui.header}>
                            <div style={ui.titleWrap}>
                                <span style={ui.badge}>{t("sanctionRuleEditModal.badge")}</span>
                                <div style={{ minWidth: 0 }}>
                                    <h5 style={ui.title}>{t("sanctionRuleEditModal.title")}</h5>
                                    <p style={ui.sub}>{t("sanctionRuleEditModal.subtitle")}</p>
                                </div>
                            </div>

                            <button
                                type="button"
                                style={ui.closeBtn}
                                onClick={onClose}
                                aria-label={t("sanctionRuleEditModal.close")}
                                title={t("sanctionRuleEditModal.close")}
                            >
                                <i className="fas fa-times" style={{ color: colors.textOnPrimary }} />
                            </button>
                        </div>

                        <div
                            className="modal-body"
                            style={{
                                ...ui.body,
                                maxHeight: "calc(100vh - 220px)",
                                overflowY: "auto",
                            }}
                        >
                            <div style={ui.section}>
                                <div style={ui.grid2}>
                                    <div>
                                        <label style={ui.label}>{t("sanctionRuleEditModal.fields.ruleName")}</label>
                                        <input
                                            className="form-control"
                                            style={ui.input}
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            placeholder={t("sanctionRuleEditModal.fields.ruleNamePlaceholder")}
                                        />
                                    </div>

                                    <div>
                                        <label style={ui.label}>
                                            {t("sanctionRuleEditModal.fields.maxAccumulatedYellowCards")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={ui.input}
                                            value={maxYellow}
                                            min={0}
                                            onChange={(e) => setMaxYellow(e.target.value)}
                                            required
                                        />
                                        <div style={ui.hint}>
                                            {t("sanctionRuleEditModal.fields.maxAccumulatedYellowCardsHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ height: 12 }} />

                                <div style={ui.grid3}>
                                    <div>
                                        <label style={ui.label}>
                                            {t("sanctionRuleEditModal.fields.redCardSuspensionGames")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={ui.input}
                                            value={redSusp}
                                            min={0}
                                            onChange={(e) => setRedSusp(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label style={ui.label}>
                                            {t("sanctionRuleEditModal.fields.accumulatedYellowSuspensionGames")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={ui.input}
                                            value={yellowSusp}
                                            min={0}
                                            onChange={(e) => setYellowSusp(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label style={ui.label}>
                                            {t("sanctionRuleEditModal.fields.doubleYellowSuspensionGames")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            style={ui.input}
                                            value={doubleYellowSusp}
                                            min={0}
                                            onChange={(e) => setDoubleYellowSusp(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ height: 12 }} />

                            <div style={ui.section}>
                                <div style={{ display: "grid", gap: 10 }}>
                                    <ToggleCard
                                        id={`${idPrefix}-resetOnRed`}
                                        checked={resetOnRed}
                                        onChange={() => setResetOnRed((v) => !v)}
                                        title={t("sanctionRuleEditModal.toggles.resetOnRed.title")}
                                        description={t("sanctionRuleEditModal.toggles.resetOnRed.description")}
                                    />

                                    <ToggleCard
                                        id={`${idPrefix}-resetOnDoubleYellow`}
                                        checked={resetOnDoubleYellow}
                                        onChange={() => setResetOnDoubleYellow((v) => !v)}
                                        title={t("sanctionRuleEditModal.toggles.resetOnDoubleYellow.title")}
                                        description={t("sanctionRuleEditModal.toggles.resetOnDoubleYellow.description")}
                                    />

                                    <ToggleCard
                                        id={`${idPrefix}-overrideAccum`}
                                        checked={overrideAccum}
                                        onChange={() => setOverrideAccum((v) => !v)}
                                        title={t("sanctionRuleEditModal.toggles.overrideAccumulation.title")}
                                        description={t("sanctionRuleEditModal.toggles.overrideAccumulation.description")}
                                    />

                                    <ToggleCard
                                        id={`${idPrefix}-resetOnStageChange`}
                                        checked={resetOnStageChange}
                                        onChange={() => setResetOnStageChange((v) => !v)}
                                        title={t("sanctionRuleEditModal.toggles.resetOnStageChange.title")}
                                        description={t("sanctionRuleEditModal.toggles.resetOnStageChange.description")}
                                    />

                                    <ToggleCard
                                        id={`${idPrefix}-resetSanctions`}
                                        checked={resetSanctions}
                                        onChange={() => setResetSanctions((v) => !v)}
                                        title={t("sanctionRuleEditModal.toggles.resetSanctionsOnStageChange.title")}
                                        description={t("sanctionRuleEditModal.toggles.resetSanctionsOnStageChange.description")}
                                    />
                                </div>
                            </div>

                            <div style={{ height: 12 }} />

                            <div style={ui.section}>
                                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                                    <div>
                                        <div style={{ fontWeight: 900, color: "#14151a", fontSize: 13.5 }}>
                                            {t("sanctionRuleEditModal.sections.fines.title")}
                                        </div>
                                        <div style={ui.sub}>{t("sanctionRuleEditModal.sections.fines.hint")}</div>
                                    </div>
                                </div>

                                <div style={{ height: 12 }} />

                                <div style={ui.grid3}>
                                    <div>
                                        <label style={ui.label}>{t("sanctionRuleEditModal.fields.yellowCardFine")}</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            style={ui.input}
                                            value={yellowFine}
                                            onChange={(e) => setYellowFine(e.target.value)}
                                            placeholder={t("sanctionRuleEditModal.fields.yellowCardFinePlaceholder")}
                                        />
                                    </div>

                                    <div>
                                        <label style={ui.label}>{t("sanctionRuleEditModal.fields.doubleYellowFine")}</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            style={ui.input}
                                            value={doubleYellowFine}
                                            onChange={(e) => setDoubleYellowFine(e.target.value)}
                                            placeholder={t("sanctionRuleEditModal.fields.doubleYellowFinePlaceholder")}
                                        />
                                    </div>

                                    <div>
                                        <label style={ui.label}>{t("sanctionRuleEditModal.fields.redCardFine")}</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-control"
                                            style={ui.input}
                                            value={redFine}
                                            onChange={(e) => setRedFine(e.target.value)}
                                            placeholder={t("sanctionRuleEditModal.fields.redCardFinePlaceholder")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={ui.footer}>
                            <button type="button" style={{ ...ui.btn, ...ui.btnMuted }} onClick={onClose}>
                                {t("sanctionRuleEditModal.close")}
                            </button>

                            <button type="submit" style={{ ...ui.btn, ...ui.btnPrimary }} disabled={uploading}>
                                {uploading ? t("sanctionRuleEditModal.saving") : t("sanctionRuleEditModal.saveChanges")}
                            </button>
                        </div>
                    </form>

                    <style>{`
            .modal button:focus,
            .modal a:focus,
            .modal input:focus,
            .modal textarea:focus,
            .modal select:focus {
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            }

            .modal input[type="checkbox"]{
              transform: none !important;
              box-shadow: none !important;
            }

            .modal i.fas,
            .modal i.far,
            .modal i.fal,
            .modal i.fa,
            .modal i[class^="fa-"],
            .modal i[class*=" fa-"] {
              font-family: "Font Awesome 5 Free" !important;
              font-weight: 900;
            }
            .modal i.fab {
              font-family: "Font Awesome 5 Brands" !important;
              font-weight: 400;
            }
          `}</style>
                </div>
            </div>
        </div>
    );
}